



import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge, Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';
import DefaultData from '../../Common/CompanyData/DefaultData'; 
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const ServicesList = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Timing State
    const [allTimings, setAllTimings] = useState([]);

    const [showBookingModal, setShowBookingModal] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(''); 
    const [bookingNotes, setBookingNotes] = useState('');
    const [bookingLoading, setBookingLoading] = useState(false);
    
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [error, setError] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [bookingConfirmation, setBookingConfirmation] = useState(null);

    const THEME_COLOR = '#065f46';
    const currency = DefaultData.DEFAULTCURRENCY || 'NGN';

    // Helper: Format 24h to 12h AM/PM
    const formatTime12h = (timeStr) => {
        if (!timeStr) return '---';
        try {
            let [hours, minutes] = timeStr.split(':');
            let h = parseInt(hours, 10);
            const ampm = h >= 12 ? 'PM' : 'AM';
            h = h % 12 || 12;
            return `${h.toString().padStart(2, '0')}:${minutes} ${ampm}`;
        } catch (e) { return timeStr; }
    };

    // Helper: Get Today's Day ID (0 for Sunday, 1 for Monday, etc.)
    const currentDayId = new Date().getDay();

    useEffect(() => {
        checkAuthStatus();
        const queryParams = new URLSearchParams(location.search);
        const term = queryParams.get('q') || '';
        setSearchTerm(term);
        loadServices(term);
    }, [location.search]);

    const checkAuthStatus = () => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        if (token && user) {
            setIsLoggedIn(true);
            const parsedUser = JSON.parse(user);
            setCurrentUser(parsedUser);
            setCustomerName(parsedUser.name || '');
            setCustomerEmail(parsedUser.email || '');
        } else {
            setIsLoggedIn(false);
            setCurrentUser(null);
        }
    };

    const loadServices = async (searchTerm) => {
        try {
            setLoading(true);
            const [servicesRes, timingsRes] = await Promise.all([
                axios.get(config.APIACTIVEURL + config.GETSERVICETYPE),
                axios.get(config.APIACTIVEURL + config.GETALLTIMINGS)
            ]);

            const servicesData = servicesRes.data;
            const timingData = timingsRes.data || [];
            setAllTimings(timingData);

            let allServices = [];
            if (servicesData && servicesData.data) { allServices = servicesData.data || []; }
            const activeServices = allServices.filter(service => service.isActive);
            setServices(activeServices);
            
            if (searchTerm.trim()) {
                const results = activeServices.filter(service =>
                    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (service.category && service.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (service.salon?.name && service.salon.name.toLowerCase().includes(searchTerm.toLowerCase()))
                );
                setFilteredServices(results);
            } else { setFilteredServices(activeServices); }
        } catch (error) {
            console.error("Error fetching data", error);
            setServices([]); setFilteredServices([]);
        } finally { setLoading(false); }
    };

    const getTodayTimingForSalon = (salonId) => {
        return allTimings.find(t => t.salonId === salonId && t.dayOfWeek === currentDayId);
    };

    // --- NEW LOGIC: Generate slots based on Salon Open/Close Times ---
    const fetchAvailableTimeSlots = (salonId, date) => {
        const dayOfWeek = date.getDay();
        const timing = allTimings.find(t => t.salonId === salonId && t.dayOfWeek === dayOfWeek);

        if (!timing || timing.isClosed) {
            setAvailableSlots([]);
            return;
        }

        const slots = [];
        let [startH, startM] = timing.openTime.split(':').map(Number);
        let [endH, endM] = timing.closeTime.split(':').map(Number);

        let current = new Date();
        current.setHours(startH, startM, 0, 0);

        const endTime = new Date();
        endTime.setHours(endH, endM, 0, 0);

        // Generate 30-minute slots from open to close
        while (current < endTime) {
            const h24 = String(current.getHours()).padStart(2, '0');
            const m = String(current.getMinutes()).padStart(2, '0');
            const time24 = `${h24}:${m}`;

            const period = current.getHours() >= 12 ? 'PM' : 'AM';
            const h12 = current.getHours() % 12 || 12;
            const time12 = `${h12.toString().padStart(2, '0')}:${m} ${period}`;

            slots.push({ value: time24, label: time12, available: true });

            current.setMinutes(current.getMinutes() + 30);
        }
        setAvailableSlots(slots);
    };

    const handleBookClick = async (service) => {
        setSelectedService(service);
        setError('');
        setShowConfirmation(false);
        setBookingConfirmation(null);
        setSelectedTimeSlot('');
        if (!isLoggedIn) { setCustomerName(''); setCustomerEmail(''); setCustomerPhone(''); }
        
        // Use the new timing-based slot fetcher
        fetchAvailableTimeSlots(service.salonId, new Date());
        setShowBookingModal(true);
    };

    const formatToNaiveISO = (date, timeSlot) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T${timeSlot}:00`;
    };

    const handleDateChange = async (date) => {
        setSelectedDate(date);
        setSelectedTimeSlot('');
        if (selectedService) { 
            fetchAvailableTimeSlots(selectedService.salonId, date); 
        }
    };

    const validateCustomerDetails = () => {
        if (!isLoggedIn) {
            if (!customerName.trim()) { setError('Please enter your name'); return false; }
            if (!customerEmail.trim()) { setError('Please enter your email'); return false; }
        }
        return true;
    };

    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!validateCustomerDetails()) return;
        if (!selectedTimeSlot) { setError('Please select a time slot'); return; }
        setBookingLoading(true);
        try {
            const startStr = formatToNaiveISO(selectedDate, selectedTimeSlot);
            const [h, m] = selectedTimeSlot.split(':').map(Number);
            const dateCopy = new Date(selectedDate);
            dateCopy.setHours(h, m, 0, 0);
            const endDateObj = new Date(dateCopy.getTime() + (selectedService.durationMinutes || 60) * 60000);
            const endStr = formatToNaiveISO(endDateObj, `${String(endDateObj.getHours()).padStart(2, '0')}:${String(endDateObj.getMinutes()).padStart(2, '0')}`);

            const bookingData = {
                salonId: selectedService.salonId,
                serviceTypeId: selectedService.serviceTypeId,
                startTime: startStr,
                endTime: endStr,
                status: "PENDING",
                email: isLoggedIn ? currentUser.email : customerEmail,
                customerName: isLoggedIn ? currentUser.name : customerName,
                notes: bookingNotes,
                ...(customerPhone && { phoneNumber: customerPhone })
            };

            const response = await axios.post(`${config.APIACTIVEURL}${config.CREATEAPPPOINTMENT}`, bookingData);
            if (response.data.success) {
                const appointmentData = response.data.data;
                const displayTime = availableSlots.find(s => s.value === selectedTimeSlot)?.label || selectedTimeSlot;
                setBookingConfirmation({
                    appointmentNo: appointmentData?.appointmentNo || 'N/A',
                    serviceName: selectedService.name,
                    date: selectedDate.toLocaleDateString(),
                    time: displayTime,
                });
                setShowConfirmation(true);
            } else { setError(response.data.message || 'Failed to create appointment'); }
        } catch (error) { setError(error.response?.data?.message || 'An error occurred.'); } finally { setBookingLoading(false); }
    };

    const formatPrice = (price) => `${currency} ${price?.toFixed(2) || '0.00'}`;
    const tileDisabled = ({ date, view }) => view === 'month' && date < new Date().setHours(0,0,0,0);

    const handleNewSearch = (e) => {
        e.preventDefault();
        const newSearchTerm = e.target.search.value.trim();
        if (newSearchTerm) navigate(`/services/search?q=${encodeURIComponent(newSearchTerm)}`);
    };

    return (
        <div style={{ backgroundColor: '#fafaf9', minHeight: '100vh' }} className="pt-5 mt-5">
            <div className="container py-lg-4 py-2">
                
                <div className="row mb-3 mb-lg-4">
                    <div className="col-12 d-flex align-items-center">
                        <button onClick={() => navigate(-1)} className="btn border-0 p-0 me-2 shadow-none"><i className="ri-arrow-left-line fs-2"></i></button>
                        <div className="card border-0 shadow-sm p-2 flex-grow-1" style={{ borderRadius: '1rem' }}>
                            <form onSubmit={handleNewSearch}>
                                <div className="input-group">
                                    <input type="text" name="search" className="form-control border-0 shadow-none py-2" placeholder="Search services or salons..." defaultValue={searchTerm} />
                                    <button type="submit" className="btn text-white px-3" style={{ backgroundColor: THEME_COLOR, borderRadius: '0.75rem' }}><i className="ri-search-line"></i></button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-4 px-1">
                    <h2 className="h5 h-lg-3 fw-bold mb-0 border-bottom border-4 pb-1" style={{ borderColor: '#d1fae5' }}>
                        {searchTerm ? `Results for "${searchTerm}"` : 'All Services'}
                    </h2>
                    <Badge bg="light" text="dark" className="border px-3 py-2">{filteredServices.length} found</Badge>
                </div>

                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" style={{ color: THEME_COLOR }} /></div>
                ) : (
                    <div className="row g-3 g-lg-4">
                        {filteredServices.map(service => {
                            const todayTiming = getTodayTimingForSalon(service.salonId);
                            return (
                                <div className="col-12 col-md-6 col-lg-4" key={service.serviceTypeId}>
                                    <div className="card border-0 shadow-sm h-100 p-4" style={{ borderRadius: '1.5rem' }}>
                                        
                                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom border-light">
                                            <span className="extra-small text-muted fw-bold text-uppercase" style={{fontSize: '0.65rem'}}>Today's Hours</span>
                                            {todayTiming ? (
                                                todayTiming.isClosed ? (
                                                    <span className="badge bg-danger-subtle text-danger rounded-pill px-2 small">Closed</span>
                                                ) : (
                                                    <span className="fw-bold text-primary" style={{ fontSize: '0.75rem' }}>
                                                        <i className="ri-time-line me-1"></i>
                                                        {formatTime12h(todayTiming.openTime)} - {formatTime12h(todayTiming.closeTime)}
                                                    </span>
                                                )
                                            ) : (
                                                <span className="text-muted extra-small italic" style={{fontSize: '0.65rem'}}>Not set</span>
                                            )}
                                        </div>

                                        <h5 className="fw-bold mb-1">{service.name}</h5>
                                        
                                        <div className="d-flex align-items-center mb-1">
                                            <i className="ri-store-2-line me-1 text-success" style={{ fontSize: '0.9rem' }}></i>
                                            <span className="fw-bold text-muted" style={{ fontSize: '0.85rem' }}>
                                                {service.salon?.name || 'IVS TOUCH'}
                                            </span>
                                        </div>

                                        <p className="text-muted small mb-3">{service.category || 'Professional Service'}</p>
                                        
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <div>
                                                <div className="fw-bold text-success fs-5">{formatPrice(service.price)}</div>
                                                <div className="small text-muted"><i className="ri-time-line"></i> {service.durationMinutes} mins</div>
                                            </div>
                                        </div>
                                        <button 
                                            className="btn w-100 py-3 rounded-pill text-white fw-bold shadow-sm"
                                            style={{ backgroundColor: THEME_COLOR }}
                                            onClick={() => handleBookClick(service)}
                                        >
                                            Book Now <i className="ri-calendar-line ms-2"></i>
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Booking Modal */}
                <Modal show={showBookingModal} onHide={() => setShowBookingModal(false)} size="lg" centered backdrop="static" fullscreen="md-down">
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="fw-bold" style={{ color: THEME_COLOR }}>
                            {showConfirmation ? 'Confirmed!' : 'Book Appointment'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-3 p-lg-4">
                        {showConfirmation && bookingConfirmation ? (
                            <div className="text-center p-3">
                                <i className="ri-checkbox-circle-fill display-3 text-success mb-3"></i>
                                <h4 className="fw-bold">Appointment Booked!</h4>
                                <div className="card bg-light border-0 p-3 my-4 text-start small">
                                    <p className="mb-1"><strong>Appointment No:</strong> {bookingConfirmation.appointmentNo}</p>
                                    <p className="mb-1"><strong>Service:</strong> {bookingConfirmation.serviceName}</p>
                                    <p className="mb-0"><strong>Time:</strong> {bookingConfirmation.date} at {bookingConfirmation.time}</p>
                                </div>
                                <Button onClick={() => setShowBookingModal(false)} className="w-100 rounded-pill py-3 text-white border-0" style={{backgroundColor: THEME_COLOR}}>Back to List</Button>
                            </div>
                        ) : (
                            selectedService && (
                                <div className="text-start">
                                    {error && <Alert variant="danger" className="small">{error}</Alert>}
                                    <div className="p-3 mb-4 rounded-3 bg-light border">
                                        <h5 className="fw-bold mb-1 small">{selectedService.name}</h5>
                                        <div className="text-muted small mb-1"><i className="ri-store-2-line me-1"></i>{selectedService.salon?.name}</div>
                                        <span className="text-success fw-bold">{formatPrice(selectedService.price)}</span>
                                    </div>
                                    <form onSubmit={handleBookingSubmit}>
                                        <div className="mb-4">
                                            <h6 className="fw-bold mb-3 small">Customer Info</h6>
                                            {isLoggedIn ? (
                                                <div className="p-3 rounded border bg-light small"><strong>{currentUser?.name}</strong> ({currentUser?.email})</div>
                                            ) : (
                                                <div className="row g-2">
                                                    <div className="col-12 col-md-6"><Form.Control size="sm" placeholder="Your Name" value={customerName} onChange={e => setCustomerName(e.target.value)} required /></div>
                                                    <div className="col-12 col-md-6"><Form.Control size="sm" type="email" placeholder="Your Email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} required /></div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="row g-4">
                                            <div className="col-lg-6">
                                                <h6 className="fw-bold mb-3 small">Select Date</h6>
                                                <div className="d-flex justify-content-center overflow-auto border rounded p-2 bg-white">
                                                    <Calendar onChange={handleDateChange} value={selectedDate} minDate={new Date()} tileDisabled={tileDisabled} className="w-100 border-0" />
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <h6 className="fw-bold mb-3 small">Select Time</h6>
                                                <div className="row g-2 overflow-auto" style={{ maxHeight: '200px' }}>
                                                    {availableSlots.length > 0 ? availableSlots.map((slot, index) => (
                                                        <div key={index} className="col-4 col-lg-4">
                                                            <button type="button" className={`btn btn-sm w-100 py-2 ${selectedTimeSlot === slot.value ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setSelectedTimeSlot(slot.value)}>
                                                                {slot.label.replace(' ', '\n')}
                                                            </button>
                                                        </div>
                                                    )) : (
                                                        <div className="col-12">
                                                            <div className="alert alert-warning py-2 small">Salon is closed on this day.</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <Form.Control as="textarea" rows={2} className="my-4 small" placeholder="Notes (Optional)" value={bookingNotes} onChange={e => setBookingNotes(e.target.value)} />
                                        <Button type="submit" style={{ backgroundColor: THEME_COLOR }} className="w-100 py-3 text-white fw-bold rounded-pill" disabled={!selectedTimeSlot || bookingLoading}>
                                            {bookingLoading ? 'Booking...' : 'Confirm Appointment'}
                                        </Button>
                                    </form>
                                </div>
                            )
                        )}
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    );
};

export default ServicesList;